import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { HeadingOne } from "Common/Custom/HeadingOne";
import PageBody from "Common/Custom/PageBody";
import Spinner from "Common/Globle/Spinner";
import { viewCheckinWithId } from "adminApi/RoomCheckInApi/RoomCheckIn";

const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleString();
};

const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const formatCurrency = (v) => {
    const n = Number(v || 0);
    return isNaN(n) ? v : n.toLocaleString(undefined, { minimumFractionDigits: 0 });
};

const bookingStatusLabel = (status) => {
    // Adjust mapping as per your business rules
    if (status === 0) return "Pending";
    if (status === 1) return "Confirmed";
    if (status === 2) return "Cancelled";
    return String(status ?? "-");
};

const ViewRoomBooking = () => {
    const { id } = useParams();
    const [loadingFetch, setLoadingFetch] = useState(false);
    const [booking, setBooking] = useState(null);

    useEffect(() => {
        if (!id) return;

        const fetchBooking = async () => {
            setLoadingFetch(true);
            try {
                const res = await viewCheckinWithId(id);
                const data = res?.data?.data || res?.data || {};
                setBooking(data);
            } catch (err) {
                console.error("Failed to load booking", err);
            } finally {
                setLoadingFetch(false);
            }
        };

        fetchBooking();
    }, [id]);

    const rooms = booking?.rooms || [];

    return (
        <React.Fragment>
            {loadingFetch && <Spinner isSpinner />}

            <PageBody
                card={false}
                title="View Room Booking"
                headerchildren={<HeadingOne>View Room Checkin</HeadingOne>}
            >
                {!booking && !loadingFetch && (
                    <div className="alert alert-warning mb-0">
                        No booking data found.
                    </div>
                )}

                {booking && (
                    <>
                        {/* Booking / Reservation Details */}
                        <div className="card mb-3">
                            <div className="card-header fw-semibold">
                                Reservation Details
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Booking Number</label>
                                        <div>{booking.booking_number || "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Booking Status</label>
                                        <div>{bookingStatusLabel(booking.booking_status)}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Check In</label>
                                        <div>{formatDateTime(booking.checkin_date)}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Check Out</label>
                                        <div>{formatDateTime(booking.checkout_date)}</div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Booking Type (ID)</label>
                                        <div>{booking.booking_type ?? "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Booking Reference (ID)</label>
                                        <div>{booking.booking_reference ?? "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Booking Reference No</label>
                                        <div>{booking.booking_reference_no ?? "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Arrival From</label>
                                        <div>{booking.arrival_from || "-"}</div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Purpose of Visit</label>
                                        <div>{booking.purpose_of_visit || "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Remarks</label>
                                        <div>
                                            {booking.remarks === null || booking.remarks === undefined
                                                ? "-"
                                                : String(booking.remarks)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Details */}
                        <div className="card mb-3">
                            <div className="card-header fw-semibold">
                                Customer Details
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label text-muted">Customer Name</label>
                                        <div>{booking.customer_name || "-"}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label text-muted">Customer Phone</label>
                                        <div>{booking.customer_phone ?? "-"}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label text-muted">Email</label>
                                        <div>{booking.email || "-"}</div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted">Full Guest Name</label>
                                        <div>{booking.full_guest_name || "-"}</div>
                                    </div>

                                    <div className="col-md-8">
                                        <label className="form-label text-muted">Address</label>
                                        <div>{booking.address || "-"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Room Details */}
                        <div className="card mb-3">
                            <div className="card-header fw-semibold">Room Details</div>
                            <div className="card-body">
                                {rooms.length === 0 ? (
                                    <div className="text-muted">No rooms found.</div>
                                ) : (
                                    rooms.map((room, idx) => (
                                        <div key={room.book_details_id || idx} className="mb-4 border rounded">
                                            {rooms.length > 1 && (
                                                <div className="border-bottom px-3 py-2 bg-light fw-semibold">
                                                    Room {idx + 1}
                                                </div>
                                            )}

                                            <div className="p-3">
                                                <div className="row g-3">
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Room No</label>
                                                        <div>{room.room_no ?? "-"}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Room Rate</label>
                                                        <div>{formatCurrency(room.room_rate)}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Booked From</label>
                                                        <div>{room.booked_from ?? "-"}</div>
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Adults</label>
                                                        <div>{room.no_of_people ?? "-"}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Children</label>
                                                        <div>{room.children ?? "-"}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Facility Days</label>
                                                        <div>{room.extra_facility_days ?? "-"}</div>
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Check In</label>
                                                        <div>{formatDate(room.extracheck_in_date ?? '-')}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Check Out</label>
                                                        <div>{formatDate(room.extracheck_out_date ?? '-')}</div>
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Bed</label>
                                                        <div>{room.extra_bed || 0}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Person</label>
                                                        <div>{room.extra_person || 0}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Extra Child</label>
                                                        <div>{room.extra_child || 0}</div>
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Complementary</label>
                                                        <div>{room.complementary_name || "-"}</div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label text-muted">Complementary Price</label>
                                                        <div>{formatCurrency(room.complementary_price)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Billing & Payment */}
                        <div className="card mb-3">
                            <div className="card-header fw-semibold">Billing & Payment</div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Discount Reason</label>
                                        <div>{booking.discount_reason || "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Commission Percent (%)</label>
                                        <div>{booking.commission_percent ?? 0}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Commission Amount</label>
                                        <div>{formatCurrency(booking.commission_amount)}</div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Payment Method (ID)</label>
                                        <div>{booking.payment_method ?? "-"}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Advance Amount</label>
                                        <div>{formatCurrency(booking.advance_amount)}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label text-muted">Total Amount</label>
                                        <div>{formatCurrency(booking.total_amount)}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label text-muted">Advance Remarks</label>
                                        <div>{booking.advance_remarks || "-"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </PageBody>
        </React.Fragment>
    );
};

export default ViewRoomBooking;